import { create } from 'zustand';
import { db } from '../lib/database';
import { Task, TaskStatus, TaskType } from '../types';
import { useAuthStore } from './authStore';

interface TaskState {
  tasks: Task[];
  isLoading: boolean;
  error: string | null;
  fetchTasks: () => Promise<void>;
  fetchTaskById: (id: string) => Promise<Task | null>;
  getTaskById: (id: string) => Task | undefined;
  createTask: (task: Omit<Task, 'id'>) => Promise<Task>;
  updateTask: (id: string, updates: Partial<Task>) => Promise<Task>;
  updateTaskStatus: (id: string, status: TaskStatus, userName: string) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
  filterTasks: (filters: {
    type?: TaskType;
    status?: TaskStatus;
    date?: string;
    employee?: string;
    client?: string;
    isUrgent?: boolean;
  }) => Task[];
}

export const useTaskStore = create<TaskState>((set, get) => ({
  tasks: [],
  isLoading: false,
  error: null,
  
  fetchTasks: async () => {
    set({ isLoading: true, error: null });
    try {
      const tasksData = await db.getTasks();

      const tasks: Task[] = (tasksData as any[]).map((task: any) => ({
        id: task.id,
        type: task.type as TaskType,
        isUrgent: task.is_urgent || false,
        title: task.title,
        description: task.description,
        napomena: task.napomena || '',
        clientId: task.client_id,
        elevatorId: task.elevator_id,
        client: task.client_name ? {
          id: task.client_id,
          name: task.client_name,
          city: task.client_city,
          address: task.client_address,
          contact_person: task.client_contact_person,
          phone: task.client_phone,
          pib: task.client_pib || null,
          mb: task.client_mb || null,
          broj_stanova: task.client_broj_stanova ?? null,
          odrzavanje: task.client_odrzavanje || null,
          ciscenje: task.client_ciscenje || null,
          odrzavanje_liftova: task.client_odrzavanje_liftova || null,
          blagajna: task.client_blagajna || null
        } : (task.client || null),
        elevator: task.elevator || null,
        date: task.date,
        time: task.time,
        status: task.status as TaskStatus,
        assignedUsers: task.assigned_users || [],
        adminFiles: task.admin_files || [],
        workerFiles: task.worker_files || [],
        images: task.images || [],
        pauses: task.pauses || [],
        closures: task.closures || [],
        acceptances: task.acceptances || [],
        rejections: task.rejections || [],
        openings: task.openings || [],
        creations: task.creations || [],
        updates: task.updates || [],
        employeeChanges: task.employeeChanges || [],
        statusChanges: task.statusChanges || [],
        notesCount: parseInt(task.notes_count) || 0,
        comments: (task.comments || []).map((comment: any) => ({
          text: comment.text,
          author: comment.author,
          timestamp: comment.created_at
        }))
      }));

      set({ tasks, isLoading: false });
    } catch (error) {
      console.error('Error fetching tasks:', error);
      set({ error: "Failed to fetch tasks", isLoading: false });
    }
  },
  
  fetchTaskById: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const taskData = await db.getTaskById(id) as any;
      
      if (!taskData) {
        set({ isLoading: false });
        return null;
      }

      const task: Task = {
        id: taskData.id,
        type: taskData.type as TaskType,
        isUrgent: taskData.is_urgent || false,
        title: taskData.title,
        description: taskData.description,
        napomena: taskData.napomena || '',
        clientId: taskData.client_id,
        elevatorId: taskData.elevator_id,
        client: taskData.client_name ? {
          id: taskData.client_id,
          name: taskData.client_name,
          city: taskData.client_city,
          address: taskData.client_address,
          contact_person: taskData.client_contact_person,
          phone: taskData.client_phone,
          pib: taskData.client_pib || null,
          mb: taskData.client_mb || null,
          broj_stanova: taskData.client_broj_stanova ?? null,
          odrzavanje: taskData.client_odrzavanje || null,
          ciscenje: taskData.client_ciscenje || null,
          odrzavanje_liftova: taskData.client_odrzavanje_liftova || null,
          blagajna: taskData.client_blagajna || null
        } : (taskData.client || null),
        elevator: taskData.elevator || null,
        date: taskData.date,
        time: taskData.time,
        status: taskData.status as TaskStatus,
        assignedUsers: taskData.assigned_users || [],
        adminFiles: taskData.admin_files || [],
        workerFiles: taskData.worker_files || [],
        images: taskData.images || [],
        pauses: taskData.pauses || [],
        closures: taskData.closures || [],
        acceptances: taskData.acceptances || [],
        rejections: taskData.rejections || [],
        openings: taskData.openings || [],
        creations: taskData.creations || [],
        updates: taskData.updates || [],
        employeeChanges: taskData.employeeChanges || [],
        statusChanges: taskData.statusChanges || [],
        comments: (taskData.comments || []).map((comment: any) => ({
          text: comment.text,
          author: comment.author,
          timestamp: comment.created_at
        }))
      };

      // Update task in store if it exists, otherwise add it
      const existingTasks = get().tasks;
      const taskIndex = existingTasks.findIndex(t => t.id === id);
      
      if (taskIndex >= 0) {
        existingTasks[taskIndex] = task;
        set({ tasks: [...existingTasks], isLoading: false });
      } else {
        set({ tasks: [...existingTasks, task], isLoading: false });
      }

      return task;
    } catch (error) {
      console.error('Error fetching task by ID:', error);
      set({ error: "Failed to fetch task", isLoading: false });
      return null;
    }
  },
  
  getTaskById: (id: string) => {
    return get().tasks.find(task => task.id === id);
  },
  
  createTask: async (task) => {
    set({ isLoading: true, error: null });
    try {
      const taskData = {
        type: task.type,
        is_urgent: task.isUrgent || false,
        title: task.title || task.description.substring(0, 50),
        description: task.description,
        napomena: task.napomena || '',
        date: task.date,
        time: task.time,
        status: task.status,
        assigned_users: task.assignedUsers,
        admin_files: task.adminFiles,
        worker_files: task.workerFiles,
        client_id: task.clientId,
        elevator_id: task.elevatorId || null,
        created_by: task.createdBy || null
      };

      console.log('Frontend sending task data:', JSON.stringify(taskData, null, 2));

      const newTask = await db.createTask(taskData) as any;
      await get().fetchTasks();
      
      const createdTask = get().tasks.find(t => t.id === newTask.id);
      if (!createdTask) throw new Error('Task not found after creation');
      
      return createdTask;
    } catch (error) {
      console.error('Error creating task:', error);
      set({ error: "Failed to create task", isLoading: false });
      throw error;
    }
  },
  
  updateTask: async (id, updates, editEntry) => {
    set({ isLoading: true, error: null });
    try {
      const updateData: any = {};
      
      if (updates.type) updateData.type = updates.type;
      if (updates.isUrgent !== undefined) updateData.is_urgent = updates.isUrgent;
      if (updates.title) updateData.title = updates.title;
      if (updates.description) updateData.description = updates.description;
      if (updates.napomena !== undefined) updateData.napomena = updates.napomena;
      if (updates.date) updateData.date = updates.date;
      if (updates.time) updateData.time = updates.time;
      if (updates.status) updateData.status = updates.status;
      if (updates.assignedUsers) updateData.assigned_users = updates.assignedUsers;
      if (updates.date) updateData.date = updates.date;
      if (updates.time) updateData.time = updates.time;
      if (updates.status) updateData.status = updates.status;
      if (updates.assignedUsers) updateData.assigned_users = updates.assignedUsers;
      if (updates.adminFiles) updateData.admin_files = updates.adminFiles;
      if (updates.workerFiles) updateData.worker_files = updates.workerFiles;

      // Add current user as changed_by for tracking
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        updateData.changed_by = currentUser.id;
      }

      await db.updateTask(id, updateData);

      // Add comment if provided
      if (updates.comments) {
        const newComments = updates.comments.filter(comment => 
          !get().tasks.find(t => t.id === id)?.comments?.some(c => 
            c.text === comment.text && c.author === comment.author
          )
        );

        for (const comment of newComments) {
          await db.createTaskComment(id, {
            text: comment.text,
            author: comment.author
          });
        }
      }

      // NAPOMENA: Backend automatski detektuje promene i upisuje u:
      // - task_updates (ažuriranja polja)
      // - task_employee_changes (promene zaposlenih)
      // - task_status_changes (promene statusa)
      // Više se ne koristi editEntry!
      // if (editEntry) { ... } - UKLONJEN ✅

      await get().fetchTasks();
      
      const updatedTask = get().tasks.find(task => task.id === id);
      if (!updatedTask) throw new Error('Task not found after update');
      
      return updatedTask;
    } catch (error) {
      console.error('Error updating task:', error);
      set({ error: "Failed to update task", isLoading: false });
      throw error;
    }
  },
  
  updateTaskStatus: async (id, status, userName) => {
    const editEntry = {
      timestamp: new Date().toISOString(),
      user: userName,
      action: 'status_changed',
      details: `Status promenjen u: ${status}`
    };
    await get().updateTask(id, { status }, editEntry);
  },

  deleteTask: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await db.deleteTask(id);
      await get().fetchTasks();
      set({ isLoading: false });
    } catch (error) {
      console.error('Error deleting task:', error);
      set({ error: "Failed to delete task", isLoading: false });
      throw error;
    }
  },
  
  filterTasks: (filters) => {
    return get().tasks.filter(task => {
      if (filters.type && task.type !== filters.type) {
        return false;
      }
      
      if (filters.status && task.status !== filters.status) {
        return false;
      }
      
      if (filters.date && task.date !== filters.date) {
        return false;
      }
      
      if (filters.employee && !task.assignedUsers.includes(filters.employee)) {
        return false;
      }
      
      if (filters.client && !task.client.toLowerCase().includes(filters.client.toLowerCase())) {
        return false;
      }

      if (filters.isUrgent !== undefined && task.isUrgent !== filters.isUrgent) {
        return false;
      }
      
      return true;
    });
  }
}));